import { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import moment from 'moment';

import TableComponent from '../components/Table';
import CustomDateRangePicker from '../components/common/DatePicker/CustomDateRangePicker';

import useAPI from '../hooks/useAPI';
import { dateFormate, toUpperCase } from '../lib/constant';
import { formatCurrency } from '../utility/number.utils';
import styles from './Settlement.module.scss';

interface IPaginationObject {
  limit: number;
  page: number;
  search: string;
  status: string;
  from: string;
  to: string;
}

interface IGiftCardTransaction {
  referenceId: string;
  giftCardWorth: string;
  amount: string;
  status: string;
  dateTime: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  giftCardId: string;
  giftCardName: string;
}

interface IGiftCardResponse {
  result: {
    transactions: IGiftCardTransaction[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

const StatusOptions = [
  { label: 'Success', value: 'SUCCESS' },
  { label: 'Failed', value: 'FAILED' },
  { label: 'Pending', value: 'PENDING' },
];

const MAX_DATE_RANGE_DAYS = 90;

export default function GiftCard() {
  const [paginationData, setPaginationData] = useState<IPaginationObject>({
    page: 1,
    limit: 10,
    search: '',
    status: '',
    from: '',
    to: '',
  });

  const [searchInput, setSearchInput] = useState('');

  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    null,
    null,
  ]);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<string>('');

  const buildApiUrl = () => {
    const params = new URLSearchParams();
    params.append('page', String(paginationData.page));
    params.append('limit', String(paginationData.limit));
    if (paginationData.search) params.append('search', paginationData.search);
    if (paginationData.status) params.append('status', paginationData.status);
    if (paginationData.from) params.append('fromDate', paginationData.from);
    if (paginationData.to) params.append('toDate', paginationData.to);
    return `/token/transaction/history/giftcard?${params.toString()}`;
  };

  const { data: giftCardResponse, isLoading } = useAPI<void, IGiftCardResponse>(
    {
      url: buildApiUrl(),
      method: 'GET',
      dependencies: [
        paginationData.page,
        paginationData.limit,
        paginationData.search,
        paginationData.status,
        paginationData.from,
        paginationData.to,
      ],
      onSuccess: () => {},
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || 'Failed to fetch gift card history',
        );
      },
    },
  );

  const transactions = giftCardResponse?.result?.transactions || [];
  const totalCount = giftCardResponse?.result?.total || 0;

  const validateDateRange = (
    start: Date | undefined,
    end: Date | undefined,
  ): boolean => {
    if (!start || !end) return true;
    const diffInDays = moment(end).diff(moment(start), 'days');
    if (diffInDays > MAX_DATE_RANGE_DAYS) {
      setDateError(`Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days`);
      return false;
    }
    setDateError('');
    return true;
  };

  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    if (startDate && date) {
      if (validateDateRange(startDate, date)) {
        setPaginationData(prev => ({
          ...prev,
          page: 1,
          from: moment(startDate).format('YYYY-MM-DD'),
          to: moment(date).format('YYYY-MM-DD'),
        }));
      }
    } else if (!date) {
      setDateError('');
      setPaginationData(prev => ({ ...prev, page: 1, from: '', to: '' }));
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPaginationData(prev => ({ ...prev, page: 1, search: searchInput }));
  };

  const handleStatusChange = (value: string) => {
    setPaginationData(prev => ({
      ...prev,
      page: 1,
      status: value === 'all' ? '' : value,
    }));
  };

  const handleFetchMoreData = (pageSize: number, page: number) => {
    setPaginationData(prev => ({ ...prev, limit: pageSize, page }));
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setDateRange([null, null]);
    setStartDate(undefined);
    setEndDate(undefined);
    setDateError('');
    setPaginationData({
      page: 1,
      limit: 10,
      search: '',
      status: '',
      from: '',
      to: '',
    });
  };

  const hasActiveFilters =
    paginationData.search ||
    paginationData.status ||
    paginationData.from ||
    paginationData.to;

  const columns = [
    {
      label: 'Name',
      id: 'name',
      width: 150,
      renderCell: (row: IGiftCardTransaction) => (
        <span>
          {row.firstName} {row.lastName}
        </span>
      ),
    },
    {
      label: 'Email',
      id: 'email',
      width: 200,
      renderCell: (row: IGiftCardTransaction) => (
        <span>{row.email || '-'}</span>
      ),
    },
    {
      label: 'Reference ID',
      id: 'referenceId',
      width: 200,
      renderCell: (row: IGiftCardTransaction) => (
        <span>{row.referenceId || '-'}</span>
      ),
    },
    {
      label: 'Gift Card',
      id: 'giftCardName',
      width: 150,
      renderCell: (row: IGiftCardTransaction) => (
        <span>{row.giftCardName || '-'}</span>
      ),
    },
    {
      label: 'Worth',
      id: 'giftCardWorth',
      renderCell: (row: IGiftCardTransaction) =>
        row.giftCardWorth == null
          ? '-'
          : formatCurrency(parseInt(row.giftCardWorth), {
              currency: '₹',
              decimals: 2,
            }),
    },
    {
      label: 'Discounted Amount',
      id: 'discountedAmount',
      renderCell: (row: IGiftCardTransaction) =>
        row.amount === null
          ? '-'
          : formatCurrency(parseInt(row.amount), {
              currency: '₹',
              decimals: 2,
            }),
    },
    {
      label: 'Status',
      id: 'status',
      renderCell: (row: IGiftCardTransaction) => (
        <span
          className={
            row.status?.toLowerCase() === 'success'
              ? 'text-success'
              : 'text-danger'
          }
        >
          {toUpperCase(row.status || '-')}
        </span>
      ),
    },
    {
      label: 'Date',
      id: 'dateTime',
      renderCell: (row: IGiftCardTransaction) =>
        row.dateTime ? dateFormate(row.dateTime) : '-',
    },
  ];

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between">
        <div>
          <h2 className="fw-bold">Gift Card History</h2>
        </div>
      </div>

      <div className="bg-white rounded-3 p-3 mt-3">
        <div className="d-flex flex-wrap gap-3 mb-3 align-items-end">
          {/* Search */}
          <form onSubmit={handleSearchSubmit}>
            <Form.Label className="small fw-semibold mb-1">Search</Form.Label>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search by name / email"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                className="rounded-2 custom-input"
                style={{ minWidth: '200px' }}
              />
              <Button type="submit" variant="outline-secondary" size="sm">
                Go
              </Button>
            </div>
          </form>

          {/* Status Filter */}
          <div style={{ minWidth: '150px' }}>
            <Form.Label className="small fw-semibold mb-1">Status</Form.Label>
            <Form.Select
              className="rounded-2 custom-input"
              value={paginationData.status || 'all'}
              onChange={e => handleStatusChange(e.target.value)}
            >
              <option value="all">All Status</option>
              {StatusOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {/* Date Range Picker */}
          <div style={{ minWidth: '220px' }} className="d-flex flex-column">
            <Form.Label className="small fw-semibold mb-1">
              Transaction Date
            </Form.Label>
            <CustomDateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
              startDate={startDate}
              setStartDate={setStartDate}
              endDate={endDate}
              // @ts-ignore
              setEndDate={handleEndDateChange}
              placeholder="Select date range"
            />
            {dateError && (
              <Form.Text className="text-danger small">{dateError}</Form.Text>
            )}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <div className="d-flex flex-column">
              <Form.Label className="small fw-semibold mb-1 custom-filter" />
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleClearFilters}
                className="rounded-2 custom-filter-button"
              >
                Clear Filters
              </Button>
            </div>
          )}
        </div>

        <div className={styles.tableWrapper}>
          <TableComponent
            data={transactions}
            columns={columns}
            totalCount={totalCount}
            fetchMore={handleFetchMoreData}
            isServerSidePagination
            perPageSize={paginationData.limit}
            activePage={paginationData.page}
            loading={isLoading}
          />
        </div>
      </div>
    </>
  );
}
